import { useCallback, useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Loader2 } from "lucide-react";
import { AppShell } from "@/components/Layout";
import { KPI, Overline, StatusPill } from "@/components/Bits";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

export default function Admin() {
  const { t, ts } = useI18n();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [samples, setSamples] = useState([]);
  const [clients, setClients] = useState([]);
  const [labs, setLabs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tickets, setTickets] = useState([]);

  const load = useCallback(async () => {
    try {
      const [d, s, c, l, a, sup] = await Promise.all([
        api.get("/admin/dashboard"), api.get("/admin/samples"), api.get("/admin/clients"),
        api.get("/admin/labs"), api.get("/admin/audit-logs"), api.get("/admin/support"),
      ]);
      setDash(d.data); setSamples(s.data.samples); setClients(c.data.clients);
      setLabs(l.data.labs); setLogs(a.data.logs); setTickets(sup.data.tickets);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, e.message));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleLab = async (code, active) => {
    await api.patch(`/admin/labs/${code}`, { active: !active });
    toast.success(t("admin.labUpdated"));
    load();
  };

  const nav = [
    { key: "dashboard", label: t("admin.dashboard") },
    { key: "samples", label: t("admin.samples") },
    { key: "clients", label: t("admin.clients") },
    { key: "labs", label: t("admin.labs") },
    { key: "support", label: t("admin.support") },
    { key: "logs", label: t("admin.logs") },
  ];

  if (!dash)
    return <AppShell title={t("admin.title")}><p className="flex items-center gap-3 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}</p></AppShell>;

  const k = dash.kpis;

  return (
    <AppShell title={t("admin.title")} nav={nav} active={tab} onNavigate={setTab} testId="admin-shell">
      {tab === "dashboard" && (
        <div data-testid="admin-dashboard">
          <Overline>{t("admin.dashboard")}</Overline>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tighter">{t("admin.ops")}</h1>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KPI label={t("admin.clients")} value={k.clients} testId="admin-kpi-clients" />
            <KPI label={t("admin.samples")} value={k.samples} testId="admin-kpi-samples" />
            <KPI label={t("admin.revenue")} value={`€${k.revenue}`} sub={`${t("admin.booked")} €${k.gross_booked}`} testId="admin-kpi-revenue" />
            <KPI label={t("admin.margin")} value={`€${k.gross_margin}`} sub={`${k.margin_pct}%`} testId="admin-kpi-margin" />
            <KPI label={t("admin.labcost")} value={`€${k.lab_cost}`} testId="admin-kpi-labcost" />
            <KPI label={t("admin.kitship")} value={`€${(k.kit_cost + k.shipping_cost).toFixed(2)}`} testId="admin-kpi-kit" />
            <KPI label={t("admin.fees")} value={`€${k.payment_fees}`} testId="admin-kpi-fees" />
            <KPI label={t("admin.turnaround")} value={k.avg_turnaround_days != null ? `${k.avg_turnaround_days} d` : "—"} sub={`${t("admin.returnRate")} ${k.return_rate_pct}%`} testId="admin-kpi-turnaround" />
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="border border-black/10 bg-white p-6">
              <Overline>{t("admin.pipeline")}</Overline>
              <div className="mt-5 h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dash.pipeline.map((p) => ({ name: p.label.split(" ")[0], count: p.count }))}>
                    <CartesianGrid stroke="#EFEFF1" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#A1A1AA" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#A1A1AA" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #E4E4E7", fontSize: 12 }} />
                    <Bar dataKey="count" fill="#0A0A0A" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="border border-black/10 bg-white p-6">
              <Overline>{t("admin.marginByAnalysis")}</Overline>
              <div className="mt-4 divide-y divide-black/5">
                {dash.margin_by_analysis.map((m) => (
                  <div key={m.code} className="flex flex-wrap items-baseline justify-between gap-3 py-3" data-testid={`admin-margin-${m.code}`}>
                    <div>
                      <p className="text-sm font-semibold">{m.name}</p>
                      <p className="font-mono-dt text-[10px] text-zinc-400">{t("admin.price")} €{m.price} · {t("admin.unitCost")} €{m.unit_cost.toFixed(2)} · {t("admin.sold")} {m.sold}</p>
                    </div>
                    <p className="font-mono-dt text-sm">€{m.unit_margin}</p>
                  </div>
                ))}
              </div>
              <Overline className="mt-6">{t("admin.byCountry")}</Overline>
              <div className="mt-3 flex flex-wrap gap-3">
                {Object.entries(dash.by_country).map(([c, n]) => (
                  <span key={c} className="border border-black/10 px-3 py-1.5 font-mono-dt text-xs">{c} · {n}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "samples" && (
        <div className="overflow-x-auto border border-black/10 bg-white" data-testid="admin-samples">
          <table className="w-full min-w-[900px] text-sm">
            <thead><tr className="border-b border-black/10 text-left">
              {["Sample ID", t("admin.client"), t("lab.analysis"), t("admin.lab"), t("lab.status"), t("admin.price"), t("admin.costs")].map((h) => (
                <th key={h} className="px-4 py-3 overline text-[9px]">{h}</th>))}
            </tr></thead>
            <tbody>
              {samples.map((s) => (
                <tr key={s.sample_id} className="border-b border-black/5" data-testid={`admin-sample-${s.sample_id}`}>
                  <td className="px-4 py-3 font-mono-dt text-xs">{s.sample_id}</td>
                  <td className="px-4 py-3 text-xs">{s.user_email || t("admin.anonymised")}</td>
                  <td className="px-4 py-3">{s.analysis_name}</td>
                  <td className="px-4 py-3 text-xs">{s.lab_code}</td>
                  <td className="px-4 py-3"><StatusPill>{ts(s.status)}</StatusPill></td>
                  <td className="px-4 py-3 font-mono-dt text-xs">€{s.price}</td>
                  <td className="px-4 py-3 font-mono-dt text-[10px] text-zinc-500">
                    lab €{s.costs?.lab} · kit €{s.costs?.kit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "clients" && (
        <div className="overflow-x-auto border border-black/10 bg-white" data-testid="admin-clients">
          <table className="w-full min-w-[600px] text-sm">
            <thead><tr className="border-b border-black/10 text-left">
              {[t("form.name"), t("form.email"), t("form.country"), t("portal.analyses"), t("admin.since")].map((h) => <th key={h} className="px-4 py-3 overline text-[9px]">{h}</th>)}
            </tr></thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-black/5">
                  <td className="px-4 py-3">{c.name}</td>
                  <td className="px-4 py-3 text-xs">{c.email}</td>
                  <td className="px-4 py-3 font-mono-dt text-xs">{c.country}</td>
                  <td className="px-4 py-3 font-mono-dt text-xs">{c.samples}</td>
                  <td className="px-4 py-3 font-mono-dt text-[10px] text-zinc-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "labs" && (
        <div className="grid gap-px bg-black/10 md:grid-cols-3" data-testid="admin-labs">
          {labs.map((l) => (
            <div key={l.code} className="bg-white p-6" data-testid={`admin-lab-${l.code}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="overline text-[9px]">{l.country} · {l.regions.join(" ")}</p>
                  <h3 className="mt-3 font-display text-2xl font-light">{l.name}</h3>
                </div>
                <StatusPill status={l.active ? "optimal" : "critical"}>{l.active ? t("admin.active") : t("admin.inactive")}</StatusPill>
              </div>
              <p className="mt-4 text-xs text-zinc-600">{l.address}</p>
              <div className="mt-4 grid gap-1.5 font-mono-dt text-[11px] text-zinc-500">
                <p>{t("admin.turnaround")} {l.turnaround_days} d · {t("admin.capacity")} {l.capacity_per_week}/wk</p>
                <p>{t("admin.assignedShort")} {l.assigned} · api {l.api_enabled ? "on" : "off"}</p>
                <p>{t("admin.cost")} {Object.entries(l.cost_per_analysis).map(([k, v]) => `${k} €${v}`).join(" · ")}</p>
                <p className="truncate">key {l.api_key}</p>
              </div>
              <button onClick={() => toggleLab(l.code, l.active)} data-testid={`admin-lab-toggle-${l.code}`}
                className="mt-5 rounded-full border border-black/20 px-4 py-2 text-[12px] font-semibold hover:bg-zinc-50">
                {l.active ? t("admin.deactivate") : t("admin.activate")}
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "support" && (
        <div className="border border-black/10 bg-white p-6" data-testid="admin-support">
          <Overline>{t("admin.tickets")}</Overline>
          <div className="mt-4 divide-y divide-black/5">
            {tickets.map((s, i) => (
              <div key={i} className="py-4">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <p className="text-sm font-semibold">{s.ticket_ref} · {s.topic}</p>
                  <p className="font-mono-dt text-[10px] text-zinc-400">{new Date(s.created_at).toLocaleString()}</p>
                </div>
                <p className="mt-1 text-xs text-zinc-500">{s.name} · {s.email}</p>
                <p className="mt-2 text-sm text-zinc-700">{s.message}</p>
              </div>
            ))}
            {tickets.length === 0 && <p className="py-3 text-sm text-zinc-500">{t("admin.noticket")}</p>}
          </div>
        </div>
      )}

      {tab === "logs" && (
        <div className="overflow-x-auto border border-black/10 bg-white" data-testid="admin-logs">
          <table className="w-full min-w-[700px] text-sm">
            <thead><tr className="border-b border-black/10 text-left">
              {[t("admin.when"), t("admin.actor"), t("admin.role"), t("admin.action"), t("admin.entity")].map((h) => <th key={h} className="px-4 py-3 overline text-[9px]">{h}</th>)}
            </tr></thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={i} className="border-b border-black/5">
                  <td className="px-4 py-2.5 font-mono-dt text-[10px] text-zinc-400">{new Date(l.at).toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-xs">{l.actor_email || "system"}</td>
                  <td className="px-4 py-2.5 font-mono-dt text-[10px]">{l.actor_role}</td>
                  <td className="px-4 py-2.5 text-xs">{l.action}</td>
                  <td className="px-4 py-2.5 font-mono-dt text-[10px] text-zinc-500">{l.entity_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
